import { useCallback, useEffect, useRef, useState } from 'react';
import { LoadingState } from './types';

const useLazyRequest = <Props, Response>(
  request: (props: Props) => Promise<Response>,
): {
  loading: LoadingState;
  error?: Error;
  data?: Response;
  execute: (params: Props) => void;
} => {
  const abortController = useRef(new AbortController());
  const requestRef = useRef(request);
  const [loading, setLoading] = useState(LoadingState.initial);
  const [data, setData] = useState<Response | undefined>();
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const execute = useCallback((params: Props) => {
    abortController.current.abort();
    abortController.current = new AbortController();
    setLoading(LoadingState.loading);

    requestRef
      .current({
        ...params,
        signal: abortController.current.signal,
      })
      .then((data) => {
        setData(data);
        setLoading(LoadingState.success);
      })
      .catch((error) => {
        setError(error);
        setLoading(LoadingState.failed);
      });
  }, []);

  return {
    loading,
    data,
    error,
    execute,
  };
};

export default useLazyRequest;
